import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";

// ─── Password strength logic ─────────────────────────────────────────────────
const getStrength = (password) => {
  if (!password) return null;
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 2)
    return { level: "low", label: "Lemah", width: "33%", color: "#ef4444" };
  if (score <= 3)
    return { level: "medium", label: "Sedang", width: "66%", color: "#f59e0b" };
  return { level: "strong", label: "Kuat", width: "100%", color: "#00BBA7" };
};

const getRules = (password) => [
  { label: "Minimal 6 karakter", pass: password.length >= 6 },
  { label: "Minimal 1 huruf besar (A-Z)", pass: /[A-Z]/.test(password) },
  { label: "Minimal 1 angka (0-9)", pass: /[0-9]/.test(password) },
  { label: "Minimal 1 karakter spesial", pass: /[^A-Za-z0-9]/.test(password) },
];

const EyeIcon = ({ show }) =>
  show ? (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21"
      />
    </svg>
  ) : (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

// ─── Main Component ───────────────────────────────────────────────────────────
const ForgotPasswordVerifyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";
  const housingEstateId = searchParams.get("housingEstateId") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const strength = getStrength(password);
  const rules = getRules(password);
  const allValid = rules.every((r) => r.pass);
  const matched = password && confirmPassword && password === confirmPassword;
  const canSubmit = allValid && matched && !isLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsLoading(true);
    setError("");
    try {
      await api.post("/admin/auth/validate-forgot-password", {
        password,
        confirmPassword,
        housingEstateId,
        token,
        email,
      });
      setIsSuccess(true);
    } catch (err) {
      setError(
        err.userMessage || "Verifikasi gagal. Pastikan link masih berlaku.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── Success ──
  if (isSuccess) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{
          background:
            "linear-gradient(145deg, #0B4F4A 0%, #005F5A 25%, #009689 60%, #00BBA7 100%)",
        }}
      >
        <div className="text-center max-w-sm w-full">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Password Berhasil Diubah!
          </h2>
          <p className="text-sm mb-8" style={{ color: "#CBFBF1" }}>
            Password baru Anda telah berhasil disimpan. Silakan login kembali.
          </p>
          <button
            onClick={() => window.location.replace("https://admin.bwarga.id")}
            className="text-sm font-semibold px-8 py-3 rounded-xl transition-all"
            style={{ background: "white", color: "#009689" }}
            onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Kembali ke Dashboard Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{
        background:
          "linear-gradient(145deg, #0B4F4A 0%, #005F5A 25%, #009689 60%, #00BBA7 100%)",
      }}
    >
      {/* Decorative */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #00D5BE 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, #0B4F4A 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              backdropFilter: "blur(8px)",
            }}
          >
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Reset Password</h1>
          <p className="text-sm" style={{ color: "#CBFBF1" }}>
            Setel password baru untuk{" "}
            <span className="font-semibold text-white">{email}</span>
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-7"
          style={{
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(20px)",
            border: "1.5px solid rgba(255,255,255,0.5)",
            boxShadow: "0 20px 60px rgba(11,79,74,0.35)",
          }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* Password baru */}
            <div className="flex flex-col gap-1.5">
              <label
                className="text-sm font-semibold"
                style={{ color: "#005F5A" }}
              >
                Password Baru <span style={{ color: "#00BBA7" }}>*</span>
              </label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password baru"
                  className="w-full rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none transition-all"
                  style={{
                    background: "white",
                    border: "2px solid #96F7E4",
                    color: "#0B4F4A",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#00BBA7";
                    e.target.style.boxShadow = "0 0 0 3px #CBFBF1";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#96F7E4";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: "#96F7E4" }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "#009689")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "#96F7E4")}
                >
                  <EyeIcon show={showPass} />
                </button>
              </div>

              {/* Strength bar */}
              {password && (
                <div className="flex flex-col gap-1.5 mt-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: "#009689" }}>
                      Kekuatan password
                    </span>
                    <span
                      className="text-xs font-bold"
                      style={{ color: strength.color }}
                    >
                      {strength.label}
                    </span>
                  </div>
                  <div
                    className="h-1.5 rounded-full"
                    style={{ background: "#CBFBF1" }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: strength.width,
                        background: strength.color,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div className="flex flex-col gap-1.5">
              <label
                className="text-sm font-semibold"
                style={{ color: "#005F5A" }}
              >
                Konfirmasi Password <span style={{ color: "#00BBA7" }}>*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Ulangi password baru"
                  className="w-full rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none transition-all"
                  style={{
                    background: "white",
                    border: `2px solid ${confirmPassword && !matched ? "#f87171" : "#96F7E4"}`,
                    color: "#0B4F4A",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#00BBA7";
                    e.target.style.boxShadow = "0 0 0 3px #CBFBF1";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor =
                      confirmPassword && !matched ? "#f87171" : "#96F7E4";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: "#96F7E4" }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "#009689")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "#96F7E4")}
                >
                  <EyeIcon show={showConfirm} />
                </button>
              </div>
              {confirmPassword && !matched && (
                <p className="text-xs text-red-500">Password tidak cocok.</p>
              )}
              {matched && (
                <p className="text-xs font-medium" style={{ color: "#009689" }}>
                  ✓ Password cocok
                </p>
              )}
            </div>

            {/* Rules */}
            <div
              className="rounded-xl p-4 flex flex-col gap-2"
              style={{ background: "#F0FDFA", border: "1.5px solid #CBFBF1" }}
            >
              <p
                className="text-xs font-bold uppercase tracking-wider mb-1"
                style={{ color: "#009689" }}
              >
                Syarat Password
              </p>
              {rules.map((rule) => (
                <div key={rule.label} className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all"
                    style={{ background: rule.pass ? "#00BBA7" : "#CBFBF1" }}
                  >
                    {rule.pass && (
                      <svg
                        className="w-2.5 h-2.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <span
                    className="text-xs transition-colors"
                    style={{ color: rule.pass ? "#005F5A" : "#96F7E4" }}
                  >
                    {rule.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div
                className="flex items-start gap-2 rounded-xl px-4 py-3"
                style={{ background: "#FEF2F2", border: "1.5px solid #FECACA" }}
              >
                <svg
                  className="w-4 h-4 text-red-500 shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-3.5 rounded-xl font-bold text-sm transition-all"
              style={{
                background: canSubmit
                  ? "linear-gradient(135deg, #00D5BE, #009689)"
                  : "#CBFBF1",
                color: canSubmit ? "white" : "#96F7E4",
                boxShadow: canSubmit
                  ? "0 4px 14px rgba(0,187,167,0.35)"
                  : "none",
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Menyimpan...
                </span>
              ) : (
                "Simpan Password Baru"
              )}
            </button>
          </form>
        </div>

        <p
          className="text-center text-xs mt-5"
          style={{ color: "rgba(203,251,241,0.5)" }}
        >
          Link reset password hanya berlaku satu kali dan memiliki batas waktu.
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordVerifyPage;
